import { useCallback, useEffect, useState } from 'react';
import { api, type AuthState } from './lib/api.ts';
import { matchRoute, useRoute } from './lib/router.ts';
import { listenForNotificationNavigation } from './lib/push.ts';
import { AppShell } from './components/AppShell.tsx';
import { Spinner } from './components/ui.tsx';
import { SignIn } from './screens/SignIn.tsx';
import { Today } from './screens/Today.tsx';
import { CheckIn } from './screens/CheckIn.tsx';
import { Experiments } from './screens/Experiments.tsx';
import { ExperimentDetail } from './screens/ExperimentDetail.tsx';
import { Wizard } from './screens/Wizard.tsx';
import { Report } from './screens/Report.tsx';
import { DoctorSummary } from './screens/DoctorSummary.tsx';
import { Medications, Profile, RecordNav, Scripts, Timeline } from './screens/Record.tsx';
import { Settings } from './screens/Settings.tsx';

/** Screens that render their own heading rather than the shell's header bar. */
const NO_HEADER = new Set(['today']);

const TITLES: Record<string, string> = {
  checkin: 'Check in',
  experiments: 'Experiments',
  experiment: 'Experiment',
  wizard: 'New experiment',
  report: 'Final report',
  doctor: 'For my doctor',
  timeline: 'Record',
  medications: 'Record',
  scripts: 'Record',
  profile: 'Record',
  settings: 'Settings',
};

const RECORD_SCREENS = new Set(['timeline', 'medications', 'scripts', 'profile']);

export function App() {
  const [path, navigate] = useRoute();
  const [auth, setAuth] = useState<AuthState | null>(null);

  const refreshAuth = useCallback(async () => {
    try {
      setAuth(await api.auth.state());
    } catch {
      setAuth({ needs_setup: false, signed_in: false, user: null });
    }
  }, []);

  useEffect(() => { void refreshAuth(); }, [refreshAuth]);

  // A tapped notification tells the app where to go; without this it would only
  // come to the foreground on whatever screen it was left on.
  useEffect(() => listenForNotificationNavigation(navigate), [navigate]);

  if (!auth) return <div className="min-h-dvh grid place-items-center"><Spinner /></div>;

  if (!auth.signed_in) {
    return <SignIn state={auth} onSignedIn={() => void refreshAuth()} />;
  }

  const route = matchRoute(path);
  const back = backFor(route.screen, route.id);

  return (
    <AppShell
      screen={route.screen}
      navigate={navigate}
      {...(NO_HEADER.has(route.screen) ? {} : { title: TITLES[route.screen] ?? 'HealthLab' })}
      {...(back ? { back } : {})}
    >
      {RECORD_SCREENS.has(route.screen) && <div className="mb-4"><RecordNav screen={route.screen} navigate={navigate} /></div>}

      {route.screen === 'today' && <Today navigate={navigate} />}
      {route.screen === 'experiments' && <Experiments navigate={navigate} />}
      {route.screen === 'wizard' && <Wizard navigate={navigate} />}
      {route.screen === 'experiment' && route.id && <ExperimentDetail id={route.id} navigate={navigate} />}
      {route.screen === 'checkin' && route.id && <CheckIn experimentId={route.id} navigate={navigate} />}
      {route.screen === 'report' && route.id && <Report id={route.id} navigate={navigate} />}
      {route.screen === 'doctor' && route.id && <DoctorSummary id={route.id} />}
      {route.screen === 'timeline' && <Timeline />}
      {route.screen === 'medications' && <Medications />}
      {route.screen === 'scripts' && <Scripts />}
      {route.screen === 'profile' && <Profile />}
      {route.screen === 'settings' && (
        <Settings onSignOut={() => { void api.auth.logout().then(() => { navigate('/'); void refreshAuth(); }); }} />
      )}
    </AppShell>
  );
}

function backFor(screen: string, id?: string): string | undefined {
  switch (screen) {
    case 'experiment':
    case 'wizard':
      return '/experiments';
    case 'checkin':
    case 'report':
    case 'doctor':
      return id ? `/experiments/${id}` : '/experiments';
    default:
      return undefined;
  }
}
